import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader, { cardHeaderClasses } from '@mui/material/CardHeader';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import type { ComponentProps } from 'react';

import { DataDisplayItem } from '../DataDisplayItem/DataDisplayItem';

const DATA_CARD_HEADER_CONTENT_SELECTOR = `.${cardHeaderClasses.content}`;
const DATA_CARD_BOX_SHADOW =
  '0 1px 8px 0 rgba(0, 0, 0, 0.12), 0 3px 4px 0 rgba(0, 0, 0, 0.14), 0 3px 3px -2px rgba(0, 0, 0, 0.20)';

const DataCardSkeleton = () => {
  return (
    <Stack
      direction="column"
      data-testid="data-card-skeleton-container"
      sx={{
        gap: 2,
      }}
    >
      <Skeleton height={40} width="100%" />
      <Skeleton height={40} width="100%" />
    </Stack>
  );
};

const DataCardHeader = (props: ComponentProps<typeof CardHeader> & { isLoading?: boolean }) => {
  const theme = useTheme();
  const { isLoading, ...rest } = props;

  if (isLoading) {
    return (
      <Box
        sx={{
          alignItems: 'center',
          backgroundColor: theme.palette.background.default,
          display: 'flex',
          gap: 2.5,
          height: '64px',
          paddingX: 2.5,
        }}
      >
        <Skeleton variant="circular" width={30} height={30} />
        <Skeleton width="100%" height={40} />
      </Box>
    );
  }

  return (
    <CardHeader
      titleTypographyProps={{
        color: theme.palette.secondary.main,
        fontSize: '16px',
        fontWeight: 'bold',
        variant: 'h5',
      }}
      title={props.title}
      sx={{
        [DATA_CARD_HEADER_CONTENT_SELECTOR]: {
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'row',
          gap: 1,
          justifyContent: 'space-between',
        },
        backgroundColor: theme.palette.background.default,
      }}
      {...rest}
      subheaderTypographyProps={{
        color: theme.palette.neutral[60],
      }}
    />
  );
};

type Props = {
  isLoading?: boolean;
  children: React.ReactNode;
  dataTestId?: string;
  overflowVisible?: boolean;
  slotProps?: {
    header: ComponentProps<typeof DataCardHeader>;
  };
};

export const DataCard = ({ isLoading, children, slotProps, dataTestId, overflowVisible = false }: Props) => {
  return (
    <Card
      variant="outlined"
      sx={{
        boxShadow: DATA_CARD_BOX_SHADOW,
        height: '100%',
        overflow: overflowVisible ? 'visible' : undefined,
      }}
      data-testid={dataTestId}
    >
      <DataCardHeader {...slotProps?.header} isLoading={isLoading} />
      <CardContent sx={{ overflow: overflowVisible ? 'visible' : undefined }}>
        {isLoading ? (
          <DataCardSkeleton />
        ) : (
          <Grid
            container
            sx={{
              mb: 2,
            }}
          >
            {children}
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

DataCard.Header = DataCardHeader;
DataCard.Item = DataDisplayItem;
